/** @file PartMap: the host part transform as one drawable's placement reads it (CR-006, design 22.3 a). */
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { hostPartPointBackOf } from '@zilo/pose3d-render-core/place/hostPartPointBackOf';
import { partPointOf } from '@zilo/pose3d-pose/chain/partPointOf';
import { SHAKE_WAVE_DEFAULT } from '@zilo/pose3d-pose/chain/shakeWaveDefault';
import type { PlaceHost } from '@zilo/pose3d-render-core/place/hostedPlace';

/**
 * `back(p, node)` returns a point pose moved with node `node`'s part (N, O of that node) to its own coordinates;
 * `move(p)` moves a point with the drawable's part (its node's record). Both are the identity for an exempt node.
 */
export type PartMap = { back: (p: Vec3, nodeName: string) => Vec3; move: (p: Vec3) => Vec3 };

/**
 * The map for a drawable on `node`, or null when the host passes no `partTransform` (placement then reads no map at all,
 * bit for bit as before CR-006). The shake's ppu is the authored pixels across (`tilePx[0]`, else `pixelsPerUnit`).
 */
export function partMapOf(host: PlaceHost | undefined, node: string, profile: Profile): PartMap | null {
  const parts = host?.partTransform;
  if (parts === undefined) return null;
  const clock = host?.shakeClock ?? 0;
  const wave = host?.shakeWave ?? SHAKE_WAVE_DEFAULT;
  const ppu = profile.tilePx?.[0] ?? profile.pixelsPerUnit;
  const recordOf = (name: string): (typeof parts)[string] | undefined => (Object.hasOwn(parts, name) ? parts[name] : undefined);
  const own = recordOf(node);
  return {
    back: (p, nodeName) => hostPartPointBackOf(p, recordOf(nodeName), clock, ppu, wave),
    move: (p) => partPointOf(p, own, clock, ppu, wave),
  };
}
